import type { auth } from './auth';

import { env } from '@mcdougald/env';
import { toast } from '@mcdougald/ui/src';
import { inferAdditionalFields } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

import { DOMAIN } from '@/constants/data/urls';

const getBaseUrl = () => {
  if (env.NODE_ENV === 'development') {
    return 'https://localhost:3000';
  } else if (env.NODE_ENV === 'production') {
    console.log('getBaseUrl - production', {
      env: {
        NEXT_PUBLIC_URL: env.NEXT_PUBLIC_URL,
        DOMAIN,
      },
    });
    return env.NEXT_PUBLIC_URL ?? `https://${DOMAIN}`;
  } else {
    console.warn('Unknown baseUrl Auth environment. Defaulting to localhost.');
    return 'https://localhost:3000';
  }
};

export const HTTP_STATUS_CODE = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  MULTIPLE_CHOICES: 300,
  MOVED_PERMANENTLY: 301,
  FOUND: 302,
  SEE_OTHER: 303,
  NOT_MODIFIED: 304,
  TEMPORARY_REDIRECT: 307,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  NOT_ACCEPTABLE: 406,
  PROXY_AUTHENTICATION_REQUIRED: 407,
  REQUEST_TIMEOUT: 408,
  CONFLICT: 409,
  GONE: 410,
  LENGTH_REQUIRED: 411,
  PRECONDITION_FAILED: 412,
  PAYLOAD_TOO_LARGE: 413,
  URI_TOO_LONG: 414,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RANGE_NOT_SATISFIABLE: 416,
  EXPECTATION_FAILED: 417,
  "I'M_A_TEAPOT": 418,
  MISDIRECTED_REQUEST: 421,
  UNPROCESSABLE_ENTITY: 422,
  LOCKED: 423,
  FAILED_DEPENDENCY: 424,
  TOO_EARLY: 425,
  UPGRADE_REQUIRED: 426,
  PRECONDITION_REQUIRED: 428,
  TOO_MANY_REQUESTS: 429,
  REQUEST_HEADER_FIELDS_TOO_LARGE: 431,
  UNAVAILABLE_FOR_LEGAL_REASONS: 451,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
  HTTP_VERSION_NOT_SUPPORTED: 505,
  VARIANT_ALSO_NEGOTIATES: 506,
  INSUFFICIENT_STORAGE: 507,
  LOOP_DETECTED: 508,
  NOT_EXTENDED: 510,
  NETWORK_AUTHENTICATION_REQUIRED: 511,
};

export const getHttpStatusName = (code: number): string | undefined => {
  const entry = Object.entries(HTTP_STATUS_CODE).find(
    ([, value]) => value === code,
  );
  return entry ? entry[0] : undefined;
};

// @see https://github.com/better-auth/better-auth/issues/1391
const authClient: ReturnType<typeof createAuthClient> = createAuthClient({
  plugins: [inferAdditionalFields<typeof auth>()],
  // basePath: 'api/auth/',
  // baseURL: process.env.NEXT_PUBLIC_BASE_URL ?? 'https://localhost:3000',
  baseUrl: getBaseUrl(),
  // trustedOrigins: [process.env.VERCEL_URL!],
  fetchOptions: {
    // credentials: 'include',
    // onRequest: (req): void => {
    //   // console.warn('[createAuthClient] - authClient onRequest req:', req);
    // },
    onError(e) {
      console.error('[createAuthClient] - onError e:', e);
      if (e.error.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else {
        toast.error(
          <div className={'w-[1000px]'}>
            <div className="text-sm font-semibold text-gray-700">
              {'Error signing in ' + String(getHttpStatusName(e.error.status))}
            </div>
            <div className="text-xs text-gray-500 mb-2">
              {`Please try again or contact support if the issue persists.`}
            </div>
            <pre className={'w-[700px]'}>{JSON.stringify(e, null, 2)}</pre>
          </div>,
          {
            duration: 6000,
          },
        );
      }
    },
  },
});

export const signIn: typeof authClient.signIn = authClient.signIn;
export const signOut: typeof authClient.signOut = authClient.signOut;
export const useSession: typeof authClient.useSession = authClient.useSession;

// export type Session = typeof authClient.$Infer.Session
export type User = (typeof authClient.$Infer.Session)['user'];
